import {createPoint} from './entities/entities.utils';
import {flavorTypes, colors} from './constants';

import point from './entities/point';
import ray from './entities/ray';
import line from './entities/line';
import segment from './entities/segment';
import polygon from './entities/polygon';
import parabola from './entities/parabola';
import inequality from './entities/inequality';
import circle from './entities/circle';

/**
 * Notes:
 *
 * `geometryElement` refers to the JXG.GeometryElement instance which graph elements
 * such as lines, points, and circles are derived from.
 *
 * `entities` are the data we use to describe shapes that are places on the graph,
 * as well as the data we send to the back-end for grading. They also include their `flavor`, which
 * is explained below.
 *
 * `flavor` is an attribute that helps us differentiate between a correct answer, an author-provided entity,
 * and the entities that the student has created. This is a front-end construct used to initialize entities
 * and to facilitate sending the data back to the back-end.
 *
 * `geometryInstances` are a combination of a `geometryElement`, `entity`, `flavor`, and helper methods
 * used to plot and update entities on the graph.
 *
 * `partialInstances` are geometryInstances that are in the process of being created.
 *
 * `constituentElements` are the individual geometryElements that make up a whole geometryElement.
 * For example, a line is made up of two points and one line, so a line geometryInstance has three
 * constituent elements. We use these to update the style of the `geometryElement` when, for example,
 * a element is highlighted.
 */

const entityTypes = {
  point,
  line,
  segment,
  ray,
  polygon,
  circle,
  inequality,
  parabola
};

function createCursorPoint({board, x, y}) {
  const point = createPoint({
    board,
    color: colors.preview,
    coords: [x, y],
    // This will place cursor points above all other entities
    layer: 20
  });

  let isGivingErrorFeedback = false;

  return {
    moveTo([x, y]) {
      point.moveTo([x, y]);
    },
    remove() {
      board.removeAncestors(point);
    },
    getCoords() {
      const [_, xPos, yPos] = point.coords.usrCoords;
      return [xPos, yPos];
    },
    async errorFeedback() {
      if (isGivingErrorFeedback) {
        return;
      }

      isGivingErrorFeedback = true;

      const startSize = point.getAttribute('size');
      const startColor = point.getAttribute('fillColor');

      const start = {
        size: startSize,
        fillColor: startColor
      };

      const errorSmall = {
        size: startSize,
        fillColor: colors.incorrect
      };

      const errorBig = {
        size: startSize * 2,
        fillColor: colors.incorrect
      };

      point?.setAttribute(errorBig);
      await delay();
      point?.setAttribute(errorSmall);
      await delay();
      point?.setAttribute(errorBig);
      await delay();
      point?.setAttribute(start);

      isGivingErrorFeedback = false;
    }
  };
}

async function delay(ms = 150) {
  await new Promise((res) => {
    setTimeout(res, ms);
  });
}

export {createCursorPoint, flavorTypes, entityTypes};
